import React, { useMemo, useRef, useState } from "react";
import addNotification from "helpers/notify";
import { useDatabaseWorkspace, useScopedList } from "hooks/useEstimatingDatabase";
import { DatabasePageShell } from "components/EstimatingDatabase/DatabasePageShell";
import { RecordModal } from "components/EstimatingDatabase/RecordModal";
import { ImportModeModal, ChoiceModal, confirmDialog } from "components/EstimatingDatabase/DatabaseModals";
import { formatMoney, formatNumber } from "components/EstimatingDatabase/DatabaseTable";
import { useLookup, awpOptions } from "components/EstimatingDatabase/lookups";
import {
  matchesSearch,
  nextTopCode,
  nextChildCode,
  attachChildren,
  cloneParentWithChildren,
  cloneChild,
  SCOPE_BIDLIGHT,
} from "helpers/estimatingDatabase/databaseRows";
import {
  YIELD_FIELDS,
  isMetricTemplate,
  yieldToDisplay,
  yieldFromDisplay,
  yieldUnitLabel,
  yieldForUnit,
} from "helpers/estimatingDatabase/databaseUnits";
import {
  readWorkbook,
  downloadWorkbook,
  buildToolsWorkbook,
  readToolsWorkbook,
} from "helpers/estimatingDatabase/databaseXlsx";
import { TIME_UNITS, TOOL_FIELDS, TOOL_GROUP_FIELDS } from "constants/estimatingDatabasePages";

const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v) || 0);
const toArray = (v) => (Array.isArray(v) ? v : v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : []);
const norm = (v) => String(v === undefined || v === null ? "" : v).trim().toLowerCase();

// equipment_groups: a tool type inside a category (ToolGroup); the
// category itself is only the group's Category text (ToolCategory).
export const TOOL_GROUP_CONFIG = {
  endpoint: "equipment_groups",
  fields: TOOL_GROUP_FIELDS.map((key) => ({ key })),
  fromWire: (w) => ({ ...w }),
  // SaveToolGroupToAPI posts Group = Code.
  toWire: (rec) => ({
    Code: rec.Code,
    Group: rec.Code,
    Category: rec.Category,
    Type: rec.Type,
    ...(rec.isPublic !== undefined ? { isPublic: rec.isPublic } : {}),
  }),
};

// equipments: one tool, in the group whose Code is its Group field.
export const TOOL_CONFIG = {
  endpoint: "equipments",
  fields: TOOL_FIELDS.map((key) => ({ key })),
  fromWire: (w) => {
    const out = { ...w, Awp: toArray(w.Awp), UnitCost: num(w.UnitCost), Amount: num(w.Amount) };
    YIELD_FIELDS.forEach((u) => {
      out[`Yield${u}`] = num(w[`Yield${u}`]);
    });
    return out;
  },
  toWire: (rec) => {
    const out = {
      Code: rec.Code,
      Description: rec.Description,
      Model: rec.Model,
      Group: rec.Group,
      Unit: rec.Unit,
      UnitCost: num(rec.UnitCost),
      Awp: toArray(rec.Awp),
    };
    if (rec.Amount !== undefined) out.Amount = num(rec.Amount);
    YIELD_FIELDS.forEach((u) => {
      out[`Yield${u}`] = num(rec[`Yield${u}`]);
    });
    if (rec.isPublic !== undefined) out.isPublic = rec.isPublic;
    return out;
  },
};

export default function ToolsDatabasePage() {
  const workspace = useDatabaseWorkspace();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null); // { kind: 'category'|'group'|'tool', key }
  const [expanded, setExpanded] = useState({});
  const [extraCategories, setExtraCategories] = useState([]);
  const [modal, setModal] = useState(null);
  const [choice, setChoice] = useState(null);
  const [pendingImport, setPendingImport] = useState(null);
  const [workingUnit, setWorkingUnit] = useState("SF");
  const fileRef = useRef(null);

  const groups = useScopedList(workspace, TOOL_GROUP_CONFIG);
  const tools = useScopedList(workspace, TOOL_CONFIG);
  const awps = useLookup(workspace, "awps");

  const metric = isMetricTemplate(workspace.template);
  const rate = workspace.rate || 1;
  const bidlight = workspace.scope === SCOPE_BIDLIGHT;

  // ToolsDatabaseViewModel.BuildTree: category -> group -> tool. A tool
  // whose type is missing borrows the company's type row, and is not shown
  // when there is none; types without a category are not shown.
  const tree = useMemo(() => {
    const { nodes } = attachChildren(groups.items, tools.items, {
      parentRows: groups.rawRows,
      parentKeyOf: (r) => r.Group,
      master: workspace.master,
      scope: workspace.scope,
      fromWire: TOOL_GROUP_CONFIG.fromWire,
    });
    const cats = new Map();
    extraCategories.forEach((c) => cats.set(c, []));
    nodes.forEach((n) => {
      const c = n.parent.rec.Category || "";
      if (!c) return;
      if (!cats.has(c)) cats.set(c, []);
      cats.get(c).push({ group: n.parent, tools: n.children });
    });
    return {
      categories: [...cats.entries()].map(([category, gs]) => ({ category, groups: gs })),
      orphans: [],
    };
  }, [groups.items, tools.items, groups.rawRows, extraCategories, workspace.master, workspace.scope]);

  const visibleCategories = tree.categories
    .map((c) => ({
      ...c,
      groups: c.groups.filter(
        (g) =>
          !search ||
          matchesSearch({ c: c.category }, ["c"], search) ||
          matchesSearch(g.group.rec, ["Code", "Type"], search) ||
          g.tools.some((t) => matchesSearch(t.rec, ["Code", "Description", "Model"], search))
      ),
    }))
    .filter((c) => !search || c.groups.length || matchesSearch({ c: c.category }, ["c"], search));

  const yieldFields = YIELD_FIELDS.map((u) => ({
    key: `Yield${u}`,
    label: `Yield / ${yieldUnitLabel(u, metric)} per unit of time`,
    type: "number",
    min: 0,
    toInput: (v) => Math.round(yieldToDisplay(u, v, metric) * 10000) / 10000,
    fromInput: (v) => yieldFromDisplay(u, v, metric),
  }));

  const categoryFields = [{ key: "Category", label: "Category", type: "text", required: true }];
  const groupFields = [
    { key: "Code", label: "Code", type: "readonly" },
    { key: "Category", label: "Category", type: "text", required: true },
    { key: "Type", label: "Type", type: "text", required: true },
  ];
  const toolFields = [
    { key: "Code", label: "Code", type: "readonly" },
    { key: "Description", label: "Description", type: "text", required: true },
    { key: "Model", label: "Model", type: "text" },
    { key: "Unit", label: "Unit", type: "select", required: true },
    { key: "UnitCost", label: "Unit Cost", type: "number", currency: true, positive: true },
    { key: "Awp", label: "AWP", type: "multiselect", required: true },
    ...yieldFields,
  ];

  const findGroupForActive = () => {
    if (!active) return null;
    if (active.kind === "group") return groups.items.find((g) => g.key === active.key) || null;
    if (active.kind === "tool") {
      const t = tools.items.find((x) => x.key === active.key);
      return t ? groups.items.find((g) => String(g.rec.Code) === String(t.rec.Group)) || null : null;
    }
    return null;
  };

  const startAddCategory = () => setModal({ kind: "category", mode: "add", initial: {} });
  const startAddGroup = (category) =>
    setModal({
      kind: "group",
      mode: "add",
      initial: { Code: nextTopCode(groups.items.map((g) => g.rec.Code)), Category: category },
    });
  const startAddTool = (group) => {
    const children = tools.items.filter((t) => String(t.rec.Group) === String(group.rec.Code));
    setModal({
      kind: "tool",
      mode: "add",
      initial: {
        Code: nextChildCode(group.rec.Code, children.map((t) => t.rec.Code)),
        Group: group.rec.Code,
        Unit: TIME_UNITS[0],
        Awp: [],
        UnitCost: 0,
      },
    });
  };

  // ToolsDatabaseViewModel.AddNewItem: nothing selected -> category;
  // category -> category or type; type (or tool) -> tool.
  const onAdd = () => {
    if (!active) return startAddCategory();
    if (active.kind === "category") return setChoice(active.key);
    const g = findGroupForActive();
    if (g) return startAddTool(g);
    return startAddCategory();
  };

  // ToolsDatabaseViewModel.CloneItem: a category or a type is copied with
  // everything under it (new type codes, new tool codes); a tool is copied
  // into its type.
  const onClone = () => {
    if (!active) {
      addNotification("There is no item selected to clone.", "danger");
      return;
    }
    const groupCodes = groups.items.map((g) => g.rec.Code);
    const cloneGroup = (g) => {
      const children = tools.items.filter((t) => String(t.rec.Group) === String(g.rec.Code)).map((t) => t.rec);
      const copy = cloneParentWithChildren(g.rec, children, { parentCodes: groupCodes, parentField: "Group" });
      groupCodes.push(copy.parent.Code);
      // CloneItem does not tick the copies.
      groups.add({ ...copy.parent, Group: copy.parent.Code }, { selected: false });
      copy.children.forEach((t) => tools.add(t, { selected: false }));
    };
    if (active.kind === "category") {
      groups.items.filter((g) => (g.rec.Category || "") === active.key).forEach(cloneGroup);
      return;
    }
    if (active.kind === "group") {
      const g = groups.items.find((x) => x.key === active.key);
      if (g) cloneGroup(g);
      return;
    }
    const t = tools.items.find((x) => x.key === active.key);
    if (!t) return;
    const siblings = tools.items.filter((x) => String(x.rec.Group) === String(t.rec.Group)).map((x) => x.rec.Code);
    tools.add(cloneChild(t.rec, siblings, { parentField: "Group" }), { selected: false });
  };

  const onEdit = () => {
    if (!active) {
      addNotification("There is no item selected to edit.", "danger");
      return;
    }
    // ToolsDatabaseViewModel.EditItem on a category renames it on every
    // type under it.
    if (active.kind === "category") {
      setModal({ kind: "category", mode: "edit", key: active.key, initial: { Category: active.key } });
      return;
    }
    const list = active.kind === "group" ? groups : tools;
    const it = list.items.find((x) => x.key === active.key);
    if (it) setModal({ kind: active.kind, mode: "edit", key: it.key, initial: it.rec });
  };

  const onDelete = () => {
    let groupKeys = groups.items.filter((g) => g.selected).map((g) => g.key);
    let toolKeys = tools.items.filter((t) => t.selected).map((t) => t.key);
    if (!groupKeys.length && !toolKeys.length && active) {
      if (active.kind === "group") groupKeys = [active.key];
      else if (active.kind === "tool") toolKeys = [active.key];
      else if (active.kind === "category") {
        groupKeys = groups.items.filter((g) => (g.rec.Category || "") === active.key).map((g) => g.key);
        setExtraCategories((c) => c.filter((x) => x !== active.key));
      }
    }
    const groupCodes = groups.items.filter((g) => groupKeys.includes(g.key)).map((g) => String(g.rec.Code));
    const allTools = [
      ...new Set([...toolKeys, ...tools.items.filter((t) => groupCodes.includes(String(t.rec.Group))).map((t) => t.key)]),
    ];
    if (!groupKeys.length && !allTools.length) {
      if (!active) addNotification("Select an item first", "danger");
      return;
    }
    confirmDialog({
      title: "Delete",
      content: `Delete ${groupKeys.length} tool type(s) and ${allTools.length} tool(s)? They are removed from the database when you save.`,
      okText: "Delete",
      onOk: () => {
        if (groupKeys.length) groups.remove(groupKeys);
        if (allTools.length) tools.remove(allTools);
        setActive(null);
      },
    });
  };

  const exportData = () => {
    const ticked = groups.items.some((g) => g.selected);
    const cats = visibleCategories
      .map((c) => ({
        category: c.category,
        groups: c.groups
          .filter((g) => !ticked || g.group.selected)
          .map((g) => ({ group: g.group.rec, tools: g.tools.map((t) => t.rec) })),
      }))
      .filter((c) => c.groups.length || !ticked);
    downloadWorkbook(buildToolsWorkbook(cats, { rate, metric }), "EquipmentData.xlsx");
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = readToolsWorkbook(readWorkbook(new Uint8Array(ev.target.result)), { rate, metric });
        if (!parsed.length) {
          addNotification("There is no data present in the file to import.", "danger");
          return;
        }
        setPendingImport(parsed);
      } catch (err) {
        addNotification("The file could not be read as an Excel workbook.", "danger");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const applyImport = (mode) => {
    const groupRecs = pendingImport.flatMap((c) => c.groups.map((g) => ({ ...g.group, Category: c.category })));
    const toolRecs = pendingImport.flatMap((c) =>
      c.groups.flatMap((g) => g.tools.map((t) => ({ ...t, Group: g.group.Code })))
    );
    groups.importRecords(groupRecs, mode, (r) => norm(r.Code));
    tools.importRecords(toolRecs, mode, (r) => norm(r.Code));
    addNotification(
      `${groupRecs.length} tool type(s) and ${toolRecs.length} tool(s) read from the file. Save to write them.`,
      "success"
    );
    setPendingImport(null);
  };

  const awpOpts = awpOptions(awps);
  const unitOpts = TIME_UNITS.map((u) => ({ value: u, label: u }));
  const isActive = (kind, key) => active && active.kind === kind && active.key === key;
  const unitLabel = yieldUnitLabel(workingUnit, metric);
  const statusText = (it) =>
    !bidlight && it.status !== "clean" ? (it.status === "new" ? "New" : "Edited") : "";

  const toolRow = (t, indent) => (
    <tr
      key={t.key}
      className={isActive("tool", t.key) ? "table-active" : ""}
      onClick={() => setActive({ kind: "tool", key: t.key })}
      onDoubleClick={() => workspace.canEdit && setModal({ kind: "tool", mode: "edit", key: t.key, initial: t.rec })}
      style={{ cursor: "pointer" }}
      data-testid="tool-row"
    >
      <td onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="squared-checkbox"
          checked={!!t.selected}
          onChange={(e) => {
            // Ticking a tool ticks its type (ToolsDatabaseViewModel).
            tools.select([t.key], e.target.checked);
            if (e.target.checked) {
              const parent = groups.items.find((g) => String(g.rec.Code) === String(t.rec.Group));
              if (parent) groups.select([parent.key], true);
            }
          }}
        />
      </td>
      <td className="f-14" style={{ paddingLeft: indent }}>
        {t.rec.Code}
      </td>
      <td className="f-14">{t.rec.Description}</td>
      <td className="f-14">{t.rec.Model || "--"}</td>
      <td className="f-14">
        {formatMoney(t.rec.UnitCost, rate)} / {t.rec.Unit || "--"}
      </td>
      <td className="f-14">{toArray(t.rec.Awp).join(", ") || "--"}</td>
      <td className="f-14">{formatNumber(yieldForUnit(t.rec, metric ? unitLabel : workingUnit))}</td>
      <td className="f-12 text-muted">{statusText(t)}</td>
    </tr>
  );

  return (
    <DatabasePageShell
      title="Tools & Equipment"
      workspace={workspace}
      lists={[groups, tools]}
      searchValue={search}
      setSearchValue={setSearch}
      onAdd={onAdd}
      onEdit={onEdit}
      onDelete={onDelete}
      onClone={onClone}
      onExport={exportData}
      onImport={() => fileRef.current && fileRef.current.click()}
      addLabel="Add category, tool type or tool"
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" style={{ display: "none" }} onChange={onFile} />
      <div className="d-flex align-items-center gap-2 mb-2 f-14">
        <span>Show yield per</span>
        <select
          className="form-control form-control-sm"
          style={{ width: 100 }}
          value={workingUnit}
          onChange={(e) => setWorkingUnit(e.target.value)}
        >
          {YIELD_FIELDS.map((u) => (
            <option key={u} value={u}>
              {yieldUnitLabel(u, metric)}
            </option>
          ))}
        </select>
      </div>
      <div className="table-responsive" style={{ maxHeight: "calc(100 * var(--vh) - 360px)", overflow: "auto" }}>
        <table className="table table-sm table-hover mb-0" data-testid="tools-table">
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th style={{ width: 36 }} />
              <th className="f-14">Code</th>
              <th className="f-14">Description / Type</th>
              <th className="f-14">Model</th>
              <th className="f-14">Unit Cost</th>
              <th className="f-14">AWP</th>
              <th className="f-14">Yield / {unitLabel}</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {visibleCategories.length === 0 && tree.orphans.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  {!bidlight && groups.firstLoad
                    ? "The company has no tools for this version yet. Switch to the BidLight database, tick what you want and save to copy it."
                    : "No tools for this version."}
                </td>
              </tr>
            )}
            {visibleCategories.map((c) => {
              const catKey = `cat:${c.category}`;
              const catOpen = expanded[catKey] !== false || !!search;
              return (
                <React.Fragment key={catKey}>
                  <tr
                    className={isActive("category", c.category) ? "table-active" : ""}
                    onClick={() => setActive({ kind: "category", key: c.category })}
                    style={{ cursor: "pointer", fontWeight: 700 }}
                    data-testid="tool-category-row"
                  >
                    <td />
                    <td className="f-14" colSpan={7}>
                      <span
                        className="mr-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded((x) => ({ ...x, [catKey]: !catOpen }));
                        }}
                      >
                        {catOpen ? "▾" : "▸"}
                      </span>
                      {c.category || "(no category)"}
                    </td>
                  </tr>
                  {catOpen &&
                    c.groups.map(({ group, tools: children }) => {
                      const open = expanded[group.key] || !!search;
                      return (
                        <React.Fragment key={group.key}>
                          <tr
                            className={isActive("group", group.key) ? "table-active" : ""}
                            onClick={() => setActive({ kind: "group", key: group.key })}
                            onDoubleClick={() =>
                              workspace.canEdit &&
                              setModal({ kind: "group", mode: "edit", key: group.key, initial: group.rec })
                            }
                            style={{ cursor: "pointer", fontWeight: 600 }}
                            data-testid="tool-group-row"
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="squared-checkbox"
                                checked={!!group.selected}
                                onChange={(e) => {
                                  groups.select([group.key], e.target.checked);
                                  tools.select(children.map((t) => t.key), e.target.checked);
                                }}
                              />
                            </td>
                            <td className="f-14" style={{ paddingLeft: 20 }}>
                              <span
                                className="mr-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpanded((x) => ({ ...x, [group.key]: !open }));
                                }}
                              >
                                {children.length ? (open ? "▾" : "▸") : "  "}
                              </span>
                              {group.rec.Code}
                            </td>
                            <td className="f-14" colSpan={5}>
                              {group.rec.Type}
                            </td>
                            <td className="f-12 text-muted">{statusText(group)}</td>
                          </tr>
                          {open && children.map((t) => toolRow(t, 44))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
            {tree.orphans.length > 0 && (
              <>
                <tr>
                  <td colSpan={8} className="f-12 text-muted">
                    Tools whose type is not in this version
                  </td>
                </tr>
                {tree.orphans.map((t) => toolRow(t, 12))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <ChoiceModal
        open={choice !== null}
        title="Which item do you want to add?"
        choices={[
          { value: "category", label: "Tool category" },
          { value: "group", label: "Tool type" },
        ]}
        onCancel={() => setChoice(null)}
        onChoose={(v) => {
          const category = choice;
          setChoice(null);
          if (v === "category") startAddCategory();
          else startAddGroup(category);
        }}
      />
      <RecordModal
        open={!!modal}
        title={
          modal
            ? `${modal.mode === "edit" ? "Edit" : "Add"} ${
                { category: "tool category", group: "tool type", tool: "tool" }[modal.kind]
              }`
            : ""
        }
        fields={modal ? { category: categoryFields, group: groupFields, tool: toolFields }[modal.kind] : []}
        initial={modal ? modal.initial : null}
        rate={rate}
        options={(f) => (f.key === "Awp" ? awpOpts : f.key === "Unit" ? unitOpts : [])}
        extraValidate={(rec) => {
          if (modal && modal.kind === "category") {
            const exists = tree.categories.some(
              (c) => norm(c.category) === norm(rec.Category) && !(modal.mode === "edit" && c.category === rec.Category)
            );
            if (exists) return { Category: "Tool category with the same name already present." };
          }
          return null;
        }}
        onCancel={() => setModal(null)}
        onSubmit={(rec) => {
          if (modal.kind === "category" && modal.mode === "edit") {
            if (rec.Category !== modal.key) {
              groups.items
                .filter((g) => (g.rec.Category || "") === modal.key)
                .forEach((g) => groups.edit(g.key, { Category: rec.Category }));
              setExtraCategories((c) => c.map((x) => (x === modal.key ? rec.Category : x)));
              setActive({ kind: "category", key: rec.Category });
            }
          } else if (modal.kind === "category") {
            setExtraCategories((c) => [...c, rec.Category]);
            setActive({ kind: "category", key: rec.Category });
          } else {
            const list = modal.kind === "group" ? groups : tools;
            if (modal.mode === "edit") list.edit(modal.key, rec);
            else list.add({ ...rec, isPublic: false });
          }
          setModal(null);
        }}
      />
      <ImportModeModal open={!!pendingImport} onCancel={() => setPendingImport(null)} onConfirm={applyImport} />
    </DatabasePageShell>
  );
}
